import React from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Search } from 'lucide-react';
import type { FuhrparkDriver } from '../types';

export type FuhrparkSortDir = 'asc' | 'desc';

export const FUHRPARK_TABLE_PAGE_SIZE = 25;

export const FUHRPARK_BTN_GREEN =
  'inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838] disabled:opacity-40';

export const FUHRPARK_BTN_GHOST =
  'inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-transparent bg-transparent px-3 text-sm font-medium text-app-text-muted transition-colors hover:bg-app-hover hover:text-app-text disabled:opacity-40';

export const FUHRPARK_SEARCH_INPUT_CLASS =
  'box-border h-9 w-full rounded-md border border-app-border bg-app-surface pl-8 pr-3 text-[13px] leading-none text-app-text outline-none placeholder:text-slate-400 focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20';

const AVATAR_TONES = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
];

export function DriverAvatar({
  driver,
  size = 'sm',
}: {
  driver: FuhrparkDriver;
  size?: 'sm' | 'md';
}) {
  const tone = AVATAR_TONES[((driver.color_idx % AVATAR_TONES.length) + AVATAR_TONES.length) % AVATAR_TONES.length];
  const dim = size === 'md' ? 'h-9 w-9 text-xs' : 'h-7 w-7 text-[10px]';
  return (
    <span className={`grid shrink-0 place-items-center rounded-full font-bold ${dim} ${tone.bg} ${tone.text}`}>
      {driver.initials || driver.name.slice(0, 2).toUpperCase()}
    </span>
  );
}

export function PlateBadge({ plate }: { plate: string }) {
  return (
    <span className="inline-flex max-w-full truncate rounded-md border border-app-border bg-app-surface-muted px-2 py-0.5 font-mono text-xs text-app-text-muted">
      {plate || '—'}
    </span>
  );
}

export function IconKpi({
  icon: Icon,
  iconClass,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-app-border bg-app-surface p-4">
      <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${iconClass}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-2xl font-bold text-app-text">{value}</p>
        <p className="truncate text-sm text-app-text-subtle">{label}</p>
      </div>
    </div>
  );
}

export function EmptyState({ text }: { text: string }) {
  return (
    <p className="rounded-xl border border-app-border bg-app-surface px-4 py-8 text-center text-sm text-app-text-subtle">
      {text}
    </p>
  );
}

export function FuhrparkSortableHeader({
  label,
  active,
  dir,
  onSort,
}: {
  label: string;
  active: boolean;
  dir: FuhrparkSortDir;
  onSort: () => void;
}) {
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="sticky top-0 z-10 px-4 py-2.5"
    >
      <button
        type="button"
        onClick={onSort}
        className={`group/sort inline-flex w-full items-center gap-1 text-xs font-semibold ${
          active ? 'text-app-text' : 'text-app-text-subtle hover:text-app-text'
        }`}
      >
        <span className="truncate">{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 ${active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'}`}
          aria-hidden
        />
      </button>
    </th>
  );
}

export function FuhrparkFilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium ${
        active
          ? 'border-pipedrive-blue bg-blue-50 text-pipedrive-blue'
          : 'border-app-border bg-app-surface text-app-text-muted'
      }`}
    >
      {label}
      {count != null ? (
        <span className={`rounded-full px-1.5 py-0.5 text-[11px] ${active ? 'bg-blue-100' : 'bg-app-surface-muted text-app-text-subtle'}`}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

export function FuhrparkSearchInput({
  value,
  onChange,
  placeholder = 'Suchen…',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative min-w-0 flex-1 md:w-64 md:flex-none">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        className={FUHRPARK_SEARCH_INPUT_CLASS}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type="search"
      />
    </div>
  );
}

export function FuhrparkListPagination({
  total,
  page,
  pageSize,
  onPageChange,
}: {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const pageCount = total === 0 ? 0 : Math.ceil(total / pageSize);
  if (pageCount <= 1) return null;
  const rangeFrom = (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-app-border bg-app-surface px-4 py-2.5 text-xs text-app-text-subtle">
      <span className="tabular-nums">
        {`${rangeFrom.toLocaleString('de-DE')}–${rangeTo.toLocaleString('de-DE')} von ${total.toLocaleString('de-DE')}`}
        {` · Seite ${page} / ${pageCount}`}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="inline-flex h-9 items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 font-medium text-app-text-muted hover:bg-app-hover disabled:pointer-events-none disabled:opacity-40"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          Zurück
        </button>
        <button
          type="button"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          className="inline-flex h-9 items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 font-medium text-app-text-muted hover:bg-app-hover disabled:pointer-events-none disabled:opacity-40"
        >
          Weiter
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );
}
